import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Maximize2, BedDouble, Bath, Layers, Zap, Phone, MessageCircle, Mail, X, CheckCircle2, ChevronLeft, ChevronRight, LayoutGrid, Building2, FileText, MoveVertical, ChefHat, PanelTopOpen, Sun, Snowflake, Flame, Hammer } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { PropertyMap } from "@/components/PropertyMap";
import { PropertyCard } from "@/components/PropertyCard";
import { api, imgSrc, formatPrice, AGENCY, waLink, CATEGORIES, pickLang } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const touchX = useRef(null);
  const preloadedRef = useRef([]);
  const loadingRef = useRef(null);
  const { t, lang } = useLang();

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setActive(0);
    setShown(0);
    api.get(`/properties/${id}`)
      .then((r) => {
        setP(r.data);
        api.get("/properties", { params: { category: r.data.category } })
          .then((s) => setSimilar(s.data.filter((x) => x.id !== r.data.id).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  // Precarica tutte le foto in background: il cambio con freccette/swipe è immediato.
  // I riferimenti vanno mantenuti vivi, altrimenti il browser abortisce i download
  useEffect(() => {
    if (!p?.images?.length) return;
    preloadedRef.current = [];
    p.images.slice(1).forEach((u) => {
      const im = new Image();
      im.src = imgSrc(u);
      preloadedRef.current.push(im);
    });
  }, [p]);

  // La foto precedente resta visibile finché la nuova non è pronta (mai flash bianco);
  // se è già in cache lo scambio è istantaneo
  useEffect(() => {
    if (!p?.images?.length || active === shown) return;
    const im = new Image();
    loadingRef.current = im;
    im.onload = () => setShown(active);
    im.onerror = () => setShown(active);
    im.src = imgSrc(p.images[active]);
    if (im.complete && im.naturalWidth > 0) setShown(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, p]);

  if (notFound) {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="property-not-found">
        <p className="font-serif-d text-4xl mb-6">{t("detail.notFound")}</p>
        <Link to="/immobili" className="text-[#000000] gold-underline">{t("detail.backPortfolio")}</Link>
      </div>
    );
  }

  if (!p) {
    return <div className="pt-48 pb-32 text-center text-[#5B6670] font-light" data-testid="property-loading">{t("detail.loading")}</div>;
  }

  const nImg = p.images?.length || 0;
  const nextImg = () => { if (nImg > 0) setActive((a) => (a + 1) % nImg); };
  const prevImg = () => { if (nImg > 0) setActive((a) => (a - 1 + nImg) % nImg); };
  const onSwipeStart = (e) => { if (e.touches && e.touches[0]) touchX.current = e.touches[0].clientX; };
  const onSwipeEnd = (e) => {
    const startX = touchX.current;
    touchX.current = null;
    const tch = e.changedTouches && e.changedTouches[0];
    if (startX === null || !tch) return;
    const dx = tch.clientX - startX;
    if (dx <= -40) nextImg();
    else if (dx >= 40) prevImg();
  };

  // Testi tradotti automaticamente (fallback all'italiano se la traduzione non c'è ancora)
  const title = pickLang(p, "title", lang);
  const description = pickLang(p, "description", lang);
  const features = lang !== "it" && Array.isArray(p[`features_${lang}`]) && p[`features_${lang}`].length > 0 ? p[`features_${lang}`] : p.features || [];

  const specs = [
    { icon: Maximize2, label: t("detail.sup"), value: p.mq ? `${p.mq} ${t("card.mq")}` : null },    { icon: LayoutGrid, label: t("detail.loc"), value: p.rooms },
    { icon: BedDouble, label: t("detail.cam"), value: p.bedrooms },
    { icon: Bath, label: t("detail.bagni"), value: p.bathrooms },
    { icon: Layers, label: t("detail.piano"), value: p.floor },
    { icon: Zap, label: t("detail.ce"), value: p.energy_class },
    { icon: MapPin, label: t("detail.zona"), value: p.zone },
  ].filter((s) => s.value !== null && s.value !== undefined && s.value !== "");

  // Scheda completa stile portali: mostrata dopo il testo descrittivo
  const yn = (v) => (v === "si" ? t("detail.yes") : v === "no" ? t("detail.no") : v);
  const details = [
    { icon: Building2, label: t("detail.tipo"), value: t(`fcats.${Math.max(0, CATEGORIES.findIndex((c) => c.value === p.category))}`) },
    { icon: FileText, label: t("detail.contr"), value: t(`card.${p.contract}`) },
    { icon: Hammer, label: t("detail.stato"), value: p.condition ? t(`detail.cond_${p.condition}`) : null },
    { icon: MapPin, label: t("detail.citta"), value: p.city || "Roma" },
    { icon: Maximize2, label: t("detail.sup"), value: p.mq ? `${p.mq} ${t("card.mq")}` : null },
    { icon: LayoutGrid, label: t("detail.loc"), value: p.rooms },
    { icon: BedDouble, label: t("detail.cam"), value: p.bedrooms },
    { icon: Bath, label: t("detail.bagni"), value: p.bathrooms },
    { icon: Layers, label: t("detail.piano"), value: p.floor },
    { icon: Zap, label: t("detail.ce"), value: p.energy_class },
    { icon: MoveVertical, label: t("detail.asc"), value: yn(p.elevator) },
    { icon: ChefHat, label: t("detail.cuc"), value: p.kitchen },
    { icon: PanelTopOpen, label: t("detail.balc"), value: yn(p.balcony) },
    { icon: Sun, label: t("detail.terr"), value: yn(p.terrace) },
    { icon: Snowflake, label: t("detail.clima"), value: p.air_conditioning },
    { icon: Flame, label: t("detail.risc"), value: p.heating },
  ].filter((d) => d.value !== null && d.value !== undefined && d.value !== "");

  const waText = t("detail.waMsg").replace("{ref}", p.ref).replace("{title}", title);
  const mailSubj = t("detail.mailSubj").replace("{ref}", p.ref).replace("{title}", title);

  return (
    <div data-testid="property-detail-page" className="pt-32 sm:pt-36 pb-24">
      <Seo
        title={`${title} — ${p.zone}, ${p.city || "Roma"} | ROMACASA`}
        siteName="ROMACASA"
        description={`${title}: ${p.mq ? p.mq + " mq, " : ""}${p.zone}, ${p.city || "Roma"}. ${t(`card.${p.contract}`)} — ${formatPrice(p, lang)}. Rif. ${p.ref}.`}
        image={p.images?.[0]?.startsWith("/api") ? undefined : p.images?.[0]}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/immobili" data-testid="back-to-listings" className="inline-flex items-center gap-2 text-sm text-[#5B6670] hover:text-[#000000] transition-colors mb-8">
          <ArrowLeft size={15} /> {t("detail.back")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <Reveal>
              <div
                data-testid="gallery-main"
                className="relative overflow-hidden group border border-[#E0E3E6] touch-pan-y select-none"
                onTouchStart={onSwipeStart}
                onTouchEnd={onSwipeEnd}
              >
                <button data-testid="gallery-main-image" onClick={() => setLightbox(true)} className="block w-full cursor-zoom-in">
                  <img src={imgSrc(p.images?.[shown])} alt={`${p.title} — foto ${shown + 1}`} draggable={false} className="w-full aspect-[16/10] object-cover" />
                </button>
                <span className="absolute bottom-4 right-4 bg-[#000000]/85 text-[#FAFAF7] text-xs font-mono-d px-3 py-2 pointer-events-none">{shown + 1} / {nImg || 1}</span>
                {nImg > 1 && (
                  <>
                    <button
                      data-testid="gallery-prev"
                      onClick={prevImg}
                      aria-label="Foto precedente"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-[#000000]/70 hover:bg-[#000000] text-[#FAFAF7] transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <ChevronLeft size={22} />
                    </button>
                    <button
                      data-testid="gallery-next"
                      onClick={nextImg}
                      aria-label="Foto successiva"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-[#000000]/70 hover:bg-[#000000] text-[#FAFAF7] transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <ChevronRight size={22} />
                    </button>
                  </>
                )}
              </div>
              {nImg > 1 && (
                <div data-testid="gallery-strip" className="mt-3 flex gap-2 overflow-x-auto pb-2">
                  {p.images.map((u, i) => (
                    <button
                      key={i}
                      data-testid={`gallery-thumb-${i}`}
                      onClick={() => setActive(i)}
                      aria-label={`Vai alla foto ${i + 1}`}
                      className={`shrink-0 w-20 h-20 overflow-hidden border-2 transition-all duration-200 ${i === shown ? "border-black opacity-100" : "border-transparent opacity-55 hover:opacity-100"}`}
                    >
                      <img src={imgSrc(u)} alt={`${p.title} — miniatura ${i + 1}`} draggable={false} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.08} className="mt-12">
              <Eyebrow className="mb-5">{t("detail.sintesi")}</Eyebrow>
              <div className="border border-[#E0E3E6] bg-white p-6 sm:p-8" data-testid="summary-box">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-6" data-testid="specs-grid">
                  {specs.map((s) => (
                    <div key={s.label}>
                      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A94A0]">
                        <s.icon size={13} className="text-[#000000]" /> {s.label}
                      </p>
                      <p className="mt-1.5 font-mono-d text-sm font-medium text-[#000000]">{s.value}</p>
                    </div>
                  ))}
                </div>
                {p.features?.length > 0 && (
                  <div className="mt-7 pt-6 border-t border-[#E0E3E6]">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A94A0] mb-4">{t("detail.dotaz")}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="features-list">
                      {features.map((f) => (
                        <li key={f} className="flex items-center gap-3 text-[#2B323A] font-light">
                          <CheckCircle2 size={16} className="text-[#000000] shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-12">
              <Eyebrow>{t("detail.desc")}</Eyebrow>
              <p className="mt-5 text-[#2B323A] font-light text-base sm:text-lg leading-relaxed whitespace-pre-line">{description}</p>
            </Reveal>

            {details.length > 0 && (
              <Reveal delay={0.12} className="mt-12">
                <Eyebrow className="mb-5">{t("detail.caratt")}</Eyebrow>
                <div className="border border-[#E0E3E6] bg-white p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7" data-testid="characteristics-grid">
                  {details.map((d) => (
                    <div key={d.label} className="flex items-start gap-4">
                      <d.icon size={20} strokeWidth={1.6} className="text-[#000000] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-[#000000]">{d.label}</p>
                        <p className="text-sm text-[#5B6670] font-light mt-0.5">{d.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {p.floor_plan && (
              <Reveal delay={0.18} className="mt-12">
                <Eyebrow>{t("detail.plan")}</Eyebrow>
                {p.floor_plan.toLowerCase().endsWith(".pdf") ? (
                  <a href={imgSrc(p.floor_plan)} target="_blank" rel="noreferrer" data-testid="floorplan-pdf" className="mt-5 inline-flex items-center gap-2 border border-black hover:bg-black hover:text-white px-6 py-3.5 text-sm font-medium transition-colors">
                    {t("detail.planPdf")}
                  </a>
                ) : (
                  <button data-testid="floorplan-image" onClick={() => setPlanOpen(true)} className="mt-5 block border border-[#E0E3E6] bg-white p-3 hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.18)] transition-shadow cursor-zoom-in">
                    <img src={imgSrc(p.floor_plan)} alt={`Planimetria — ${p.title}`} className="max-w-md w-full h-auto" />
                  </button>
                )}
              </Reveal>
            )}

            {p.lat && p.lng && (
              <Reveal delay={0.2} className="mt-12">
                <Eyebrow className="mb-5">{t("detail.pos")}</Eyebrow>
                <PropertyMap properties={[p]} height="380px" center={[p.lat, p.lng]} zoom={15} />
              </Reveal>
            )}
          </div>

          <aside className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-36 border border-[#E0E3E6] bg-white p-7 sm:p-8" data-testid="property-sidebar">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#000000]">{t(`card.${p.contract}`)}</span>
                  <span className="font-mono-d text-xs text-[#5B6670]">{t("detail.rif")} {p.ref}</span>
                </div>
                <h1 className="font-serif-d text-3xl sm:text-4xl font-medium leading-tight">{title}</h1>
                <p className="mt-3 flex items-center gap-2 text-sm text-[#5B6670]">
                  <MapPin size={14} className="text-[#000000]" /> {p.address ? `${p.address}, ` : ""}{p.zone} — {p.city || "Roma"}
                </p>
                <p data-testid="detail-price" className="mt-6 font-mono-d text-3xl font-semibold text-[#000000]">{formatPrice(p, lang)}</p>

                <div className="mt-8 pt-7 border-t border-[#E0E3E6] flex flex-col gap-3">
                  <a href={waLink(waText)} target="_blank" rel="noreferrer" data-testid="detail-whatsapp-button" className="flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1eb857] text-white py-4 text-sm font-medium transition-colors">
                    <MessageCircle size={16} /> {t("detail.wa")}
                  </a>
                  <a href={AGENCY.phoneHref} data-testid="detail-phone-button" className="flex items-center justify-center gap-2.5 bg-[#000000] hover:bg-[#000000] text-white py-4 text-sm font-medium transition-colors">
                    <Phone size={16} /> {AGENCY.phone}
                  </a>
                  <a href={`mailto:${AGENCY.email}?subject=${encodeURIComponent(mailSubj)}`} data-testid="detail-email-button" className="flex items-center justify-center gap-2.5 border border-[#000000] hover:bg-[#000000] hover:text-[#FAFAF7] py-4 text-sm font-medium transition-colors">
                    <Mail size={16} /> {t("detail.email")}
                  </a>
                </div>
                <p className="mt-5 text-xs text-[#8A94A0] font-light leading-relaxed">
                  {t("detail.visita")} {AGENCY.address}.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>

        {similar.length > 0 && (
          <section className="mt-24" data-testid="similar-section">
            <Reveal>
              <Eyebrow>{t("detail.interessa")}</Eyebrow>
              <h2 className="font-serif-d text-3xl sm:text-4xl font-medium mt-4 mb-10">{t("detail.simili")}</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map((s) => <PropertyCard key={s.id} p={s} />)}
            </div>
          </section>
        )}
      </div>

      {planOpen && (
        <div
          data-testid="floorplan-lightbox"
          onClick={() => setPlanOpen(false)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
        >
          <button data-testid="floorplan-close" className="absolute top-6 right-6 text-[#FAFAF7] hover:text-[#E8E8E8] transition-colors" aria-label="Chiudi">
            <X size={30} />
          </button>
          <img src={imgSrc(p.floor_plan)} alt={`Planimetria — ${p.title}`} className="max-w-full max-h-full object-contain bg-white" />
        </div>
      )}

      {lightbox && (
        <div
          data-testid="lightbox-overlay"
          onClick={() => setLightbox(false)}
          onTouchStart={onSwipeStart}
          onTouchEnd={onSwipeEnd}
          className="fixed inset-0 z-[100] bg-[#000000]/95 backdrop-blur flex items-center justify-center p-4 sm:p-10 cursor-zoom-out touch-pan-y"
        >
          <button data-testid="lightbox-close" className="absolute top-6 right-6 text-[#FAFAF7] hover:text-[#E8E8E8] transition-colors z-10" aria-label="Chiudi">
            <X size={30} />
          </button>
          {nImg > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImg(); }} aria-label="Foto precedente"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-[#FAFAF7] transition-colors z-10">
                <ChevronLeft size={26} />
              </button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); nextImg(); }} aria-label="Foto successiva"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 text-[#FAFAF7] transition-colors z-10">
                <ChevronRight size={26} />
              </button>
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#FAFAF7]/80 text-xs font-mono-d">{shown + 1} / {nImg}</span>
            </>
          )}
          <img src={imgSrc(p.images?.[shown])} alt={p.title} draggable={false} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain cursor-default select-none" />
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;
